
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Share2, Mail, Users } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface DocumentSharingProps {
  documentTitle: string;
  documentContent: string;
  documentId?: string;
}

const DocumentSharing: React.FC<DocumentSharingProps> = ({ documentTitle, documentContent, documentId }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<'view' | 'edit'>('view');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { language } = useLanguage();
  const { user } = useAuth();

  const handleShare = async () => {
    if (!email || !email.includes('@')) {
      toast.error(language === 'ur' ? 'برائے مہربانی درست ای میل درج کریں' : 'Please enter a valid email');
      return;
    }

    setIsSubmitting(true);

    try {
      const sharingId = Date.now().toString();
      
      // Create a sharing record
      const sharingRecord = {
        id: sharingId,
        documentTitle,
        recipientEmail: email,
        permission,
        sharedAt: new Date().toISOString(),
        message,
      };

      // If user is logged in and we have a document ID, save to Supabase
      if (user && documentId) {
        const { error } = await supabase
          .from('shared_documents')
          .insert({
            document_id: documentId,
            owner_id: user.id,
            recipient_email: email,
            permission,
            message,
          });
          
        if (error) throw error;
      } else {
        // Fallback to localStorage
        const sharingRecords = JSON.parse(localStorage.getItem('adaaltBotSharedDocs') || '[]');
        sharingRecords.push(sharingRecord);
        localStorage.setItem('adaaltBotSharedDocs', JSON.stringify(sharingRecords));
      }
      
      toast.success(language === 'ur' ? `${email} کے ساتھ دستاویز کامیابی سے شئیر کی گئی` : `Document shared with ${email}`);
      
      // Reset form and close dialog
      setEmail('');
      setPermission('view');
      setMessage('');
      setOpen(false);
    } catch (error: any) {
      console.error("Error sharing document:", error);
      toast.error(error.message || (language === 'ur' ? 'دستاویز شئیر کرنے میں خرابی' : 'Error sharing document'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button 
        onClick={() => setOpen(true)} 
        variant="outline" 
        size="sm"
        className="flex items-center gap-1 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
      >
        <Share2 size={16} className={language === 'ur' ? 'ml-1' : 'mr-1'} />
        {language === 'ur' ? 'شئیر کریں' : 'Share'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`sm:max-w-[425px] ${language === 'ur' ? 'font-urdu rtl' : 'font-english ltr'}`}>
          <DialogHeader>
            <DialogTitle>{language === 'ur' ? 'دستاویز شئیر کریں' : 'Share Document'}</DialogTitle>
            <DialogDescription>
              {language === 'ur' ? 'اس دستاویز کو کسی وکیل یا خاندان کے رکن کے ساتھ شئیر کریں۔' : 'Share this document with an advocate or family member.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="document-title" className="text-sm font-medium">
                {language === 'ur' ? 'دستاویز' : 'Document'}
              </label>
              <Input
                id="document-title"
                value={documentTitle}
                readOnly
                className="bg-muted"
                dir={language === 'ur' ? 'rtl' : 'ltr'}
              />
            </div>
            
            <div className="grid gap-2">
              <label htmlFor="recipient-email" className="text-sm font-medium flex items-center gap-1">
                <Mail className="h-4 w-4" />
                {language === 'ur' ? 'وصول کنندہ کا ای میل' : 'Recipient Email'}
              </label>
              <Input
                id="recipient-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={language === 'ur' ? 'ای میل درج کریں' : 'email@example.com'}
                type="email"
                dir={language === 'ur' ? 'rtl' : 'ltr'}
              />
            </div>
            
            <div className="grid gap-2">
              <label htmlFor="permission" className="text-sm font-medium flex items-center gap-1">
                <Users className="h-4 w-4" />
                {language === 'ur' ? 'اجازت' : 'Permission'}
              </label>
              <Select
                value={permission}
                onValueChange={(value: 'view' | 'edit') => setPermission(value)}
              >
                <SelectTrigger id="permission" dir={language === 'ur' ? 'rtl' : 'ltr'}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">{language === 'ur' ? 'صرف دیکھیں' : 'View only'}</SelectItem>
                  <SelectItem value="edit">{language === 'ur' ? 'ترمیم کر سکتے ہیں' : 'Can edit'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <label htmlFor="message" className="text-sm font-medium">
                {language === 'ur' ? 'پیغام (اختیاری)' : 'Message (optional)'}
              </label>
              <Input
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={language === 'ur' ? 'پیغام شامل کریں...' : 'Add a message...'}
                dir={language === 'ur' ? 'rtl' : 'ltr'}
              />
            </div>
          </div>
          
          <DialogFooter className={language === 'ur' ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={isSubmitting}>
              {language === 'ur' ? 'منسوخ کریں' : 'Cancel'}
            </Button>
            <Button onClick={handleShare} className="adalat-button" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-b-transparent"></div>
                  {language === 'ur' ? 'شئیر کر رہا ہے...' : 'Sharing...'}
                </>
              ) : (
                language === 'ur' ? 'دستاویز شئیر کریں' : 'Share Document'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentSharing;
